import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, ChevronDown, Info, XCircle } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import { Routes } from '../../routes';
import Pagination from '../../components/Pagination';
import CalendarRangeInput from '../../components/CalendarRangeInput';
import AlertNotification from '../../components/AlertNotification';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal';
import noImage from '../../assets/img/brand/noImage.png';
import apiHelper from '../../apiHelper';

const PAGE_SIZE = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ColorCircle = ({ color }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="6" cy="6" r="5.5" stroke="black" />
        <circle cx="6" cy="6" r="4.285" fill={color} />
    </svg>
);

const SortUp = ({ active }) => (
    <svg width={10} height={10} viewBox="0 0 24 24" fill={active ? '#212529' : '#6C757D'}>
        <path d="M12 8l6 6H6l6-6z" />
    </svg>
);
const SortDown = ({ active }) => (
    <svg width={10} height={10} viewBox="0 0 24 24" fill={active ? '#212529' : '#6C757D'} style={{ marginTop: '-7px' }}>
        <path d="M12 16l-6-6h12l-6 6z" />
    </svg>
);
const SortableCol = ({ label, field, sortField, sortOrder, onSort, style = {} }) => (
    <th style={{ padding: '10px', textAlign: 'left', background: '#E9ECEF', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', cursor: 'pointer', whiteSpace: 'nowrap', ...style }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {label}
            <span onClick={() => onSort(field)} style={{ display: 'flex', flexDirection: 'column', lineHeight: 0 }}>
                <SortUp   active={sortField === field && sortOrder === 'asc'}  />
                <SortDown active={sortField === field && sortOrder === 'desc'} />
            </span>
        </span>
    </th>
);

const YellowCheckbox = ({ checked, onChange, indeterminate }) => (
    <div
        onClick={onChange}
        style={{
            width: 18, height: 18, borderRadius: 4, cursor: 'pointer', flexShrink: 0,
            border:  '1.5px solid  #212529' ,
            background: (checked || indeterminate) ? '#FFEB00' : '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
    >
        {indeterminate && !checked ? (
            <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
                <rect width="10" height="2" rx="1" fill="#212529" />
            </svg>
        ) : checked ? (
            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path d="M1 5l3.5 3.5L11 1" stroke="#212529" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ) : null}
    </div>
);

const ConditionBadge = ({ condition }) =>
    condition === 'New' ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', background: '#FFEB00', color: '#212529', border: '1px solid #FFEB00', whiteSpace: 'nowrap' }}>New</span>
    ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat', background: '#FFEB00', color: '#212529', border: '1px solid #FFEB00', whiteSpace: 'nowrap' }}>Used</span>
    );

const FilterInactiveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const FilterActiveIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath="url(#clip_ec2)">
            <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
        </g>
        <defs><clipPath id="clip_ec2"><rect width="12" height="12" fill="white" transform="translate(11 5)" /></clipPath></defs>
    </svg>
);

// ─── Spinner SVG ──────────────────────────────────────────────────────────────
const SpinnerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
        style={{ animation: 'ec-spin 0.9s linear infinite', flexShrink: 0 }}>
        <circle cx="10" cy="10" r="8.75" stroke="#DEE2E6" strokeWidth="2.5"/>
        <mask id="path-2-inside-1_ec_spinner" fill="white">
            <path d="M10 18.7543C10 19.4423 10.5599 20.008 11.2425 19.9225C12.2768 19.793 13.287 19.5023 14.2358 19.0586C15.5618 18.4386 16.7355 17.535 17.674 16.4116C18.6126 15.2883 19.2931 13.9726 19.6674 12.5575C19.9353 11.5449 20.0418 10.4991 19.9853 9.45831C19.9481 8.77133 19.2918 8.32099 18.6148 8.44333C17.9377 8.56567 17.4991 9.21551 17.5079 9.90345C17.5166 10.582 17.4334 11.2605 17.2588 11.9203C16.9777 12.9829 16.4668 13.9707 15.7621 14.8142C15.0574 15.6577 14.1761 16.3361 13.1804 16.8017C12.5623 17.0907 11.9093 17.2934 11.24 17.4054C10.5615 17.5191 10 18.0663 10 18.7543Z"/>
        </mask>
        <path d="M10 18.7543C10 19.4423 10.5599 20.008 11.2425 19.9225C12.2768 19.793 13.287 19.5023 14.2358 19.0586C15.5618 18.4386 16.7355 17.535 17.674 16.4116C18.6126 15.2883 19.2931 13.9726 19.6674 12.5575C19.9353 11.5449 20.0418 10.4991 19.9853 9.45831C19.9481 8.77133 19.2918 8.32099 18.6148 8.44333C17.9377 8.56567 17.4991 9.21551 17.5079 9.90345C17.5166 10.582 17.4334 11.2605 17.2588 11.9203C16.9777 12.9829 16.4668 13.9707 15.7621 14.8142C15.0574 15.6577 14.1761 16.3361 13.1804 16.8017C12.5623 17.0907 11.9093 17.2934 11.24 17.4054C10.5615 17.5191 10 18.0663 10 18.7543Z"
            stroke="#6C757D" strokeWidth="5" strokeMiterlimit="2.9238" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="14 1" mask="url(#path-2-inside-1_ec_spinner)"/>
    </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EditCampaign() {
    const history  = useHistory();
    const location = useLocation();
    const campaign = location.state?.campaign || {};

    // ── Basic Info ──
    const [campaignName, setCampaignName] = useState(campaign.name || '');
    const [startDate,    setStartDate]    = useState(campaign.promoStart ? new Date(campaign.promoStart) : null);
    const [endDate,      setEndDate]      = useState(campaign.promoEnd   ? new Date(campaign.promoEnd)   : null);

    // ── Items ──
    const [items,        setItems]        = useState([]);
    const [itemsLoading, setItemsLoading] = useState(true);
    const [search,       setSearch]       = useState('');
    const [sortField,    setSortField]    = useState(null);
    const [sortOrder,    setSortOrder]    = useState('asc');
    const [page,         setPage]         = useState(1);
    const [selected,     setSelected]     = useState({});

    // ── Condition filter ──
    const [filterOpen,  setFilterOpen]  = useState(false);
    const [pendingNew,  setPendingNew]  = useState(true);
    const [pendingUsed, setPendingUsed] = useState(true);
    const [appliedNew,  setAppliedNew]  = useState(true);
    const [appliedUsed, setAppliedUsed] = useState(true);
    const filterBtnRef   = useRef(null);
    const filterPopupRef = useRef(null);
    const filterActive   = !(appliedNew && appliedUsed);

    // ── UI state ──
    const [showRemoveModal,  setShowRemoveModal]  = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [discardAction,    setDiscardAction]    = useState('navigate'); // 'navigate' | 'remove'
    const [isSaving,         setIsSaving]         = useState(false);
    const [fieldErrors,      setFieldErrors]      = useState({});
    const [showOverlapCol,   setShowOverlapCol]   = useState(false);
    const [toast,            setToast]            = useState(null); // { type, message }

    // ── Fetch campaign items from API ──
    useEffect(() => {
        if (!campaign.id) { setItemsLoading(false); return; }
        setItemsLoading(true);
        apiHelper(`sales/promotional-campaigns/${campaign.id}/items/`, 'GET')
            .then(res => res.ok ? res.json() : [])
            .then(data => setItems(Array.isArray(data) ? data : (data.results ?? [])))
            .catch(() => setItems([]))
            .finally(() => setItemsLoading(false));
    }, [campaign.id]);

    // ── Close filter on outside click ──
    useEffect(() => {
        const handleClick = (e) => {
            if (
                filterPopupRef.current && !filterPopupRef.current.contains(e.target) &&
                filterBtnRef.current  && !filterBtnRef.current.contains(e.target)
            ) setFilterOpen(false);
        };
        if (filterOpen) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [filterOpen]);

    // ── Sort ──
    const handleSort = (field) => {
        if (sortField === field) setSortOrder(p => p === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortOrder('asc'); }
        setPage(1);
    };

    // ── Filtered + sorted items ──
    const filtered = useMemo(() => {
        let data = [...items];
        if (search) data = data.filter(i => i.model.toLowerCase().includes(search.toLowerCase()));
        const conds = [];
        if (appliedNew)  conds.push('New');
        if (appliedUsed) conds.push('Used');
        if (conds.length > 0 && conds.length < 2) data = data.filter(i => conds.includes(i.condition));
        if (sortField) {
            data.sort((a, b) => {
                const aV = a[sortField] ?? '', bV = b[sortField] ?? '';
                if (aV < bV) return sortOrder === 'asc' ? -1 : 1;
                if (aV > bV) return sortOrder === 'asc' ?  1 : -1;
                return 0;
            });
        }
        return data;
    }, [items, search, appliedNew, appliedUsed, sortField, sortOrder]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const rangeStart = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd   = Math.min(page * PAGE_SIZE, filtered.length);

    // ── Select all on page ──
    const allPageSelected  = pageData.length > 0 && pageData.every(i => selected[i.id]);
    const somePageSelected = pageData.some(i => selected[i.id]);
    const selectedCount    = Object.keys(selected).filter(k => selected[k]).length;

    const toggleAll = () => {
        if (allPageSelected) {
            setSelected(prev => { const n = { ...prev }; pageData.forEach(i => delete n[i.id]); return n; });
        } else {
            setSelected(prev => { const n = { ...prev }; pageData.forEach(i => n[i.id] = true); return n; });
        }
    };

    // ── Filter actions ──
    const applyFilter = () => { setAppliedNew(pendingNew); setAppliedUsed(pendingUsed); setFilterOpen(false); setPage(1); };
    const clearFilter = () => { setPendingNew(true); setPendingUsed(true); setAppliedNew(true); setAppliedUsed(true); setFilterOpen(false); setPage(1); };

    // ── Remove from campaign (actual action after final confirmation) ──
    const handleActualRemove = () => {
        setItems(prev => prev.filter(i => !selected[i.id]));
        setSelected({});
        setToast({ type: 'success', message: 'Promotional Pricing Saved Successfully' });
    };

    // ── Validate and Save ──
    const handleValidateAndSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        setFieldErrors({});
        setShowOverlapCol(false);
        setToast(null);

        await new Promise(r => setTimeout(r, 1500));

        // Simulate: name conflict only when name contains "existing"
        const nameConflict = campaignName.toLowerCase().includes('existing');
        // Overlap is always present (items 1-5 have overlapCampaign data)
        const hasOverlap = true;

        if (nameConflict || hasOverlap) {
            const errs = {};
            if (nameConflict) errs.campaignName = 'Campaign Name Already Exists';
            errs.promotionalPeriod = 'Some items have overlapping campaign in this period';
            setFieldErrors(errs);
            setShowOverlapCol(true);
            setToast({ type: 'error', message: 'Error Saving' });
            setIsSaving(false);
        } else {
            setToast({ type: 'success', message: 'Promotional Pricing Saved Successfully' });
            setIsSaving(false);
            setTimeout(() => history.push(Routes.TradeInPricingPromotional.path), 1500);
        }
    };

    // ── Discard confirmation (handles both navigate-away and remove-items actions) ──
    const handleDiscardConfirm = () => {
        setShowDiscardModal(false);
        if (discardAction === 'remove') {
            handleActualRemove();
        } else {
            history.push(Routes.TradeInPricingPromotional.path);
        }
    };

    const openDiscardForNavigation = () => {
        setDiscardAction('navigate');
        setShowDiscardModal(true);
    };

    const openDiscardForRemove = () => {
        setShowRemoveModal(false);
        setDiscardAction('remove');
        setShowDiscardModal(true);
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="company-body new-look stock-wrap bg-checkbox" style={{ paddingBottom: selectedCount > 0 ? 80 : 24 }}>

            {/* ── CSS for spinner ── */}
            <style>{`@keyframes ec-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            {/* ── Toast ── */}
            {toast && (
                <AlertNotification
                    type={toast.type}
                    message={toast.message}
                    onClose={() => setToast(null)}
                />
            )}

            {/* ── Breadcrumb ── */}
            <div className="breadcrumb">
                <span className="breadcrumb-item">Pricing</span>
                <span className="breadcrumb-item" style={{ cursor: 'pointer' }} onClick={openDiscardForNavigation}>
                   Trade-In Pricing
                </span>
                <span className="breadcrumb-item">Edit Campaign</span>
            </div>

            <div className="employee-tabs-container">
                <div className="tab-content">
                    <div className="info-section">
                        <div  style={{ overflow: 'unset' }}>

                            {/* ── Page header ── */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24,background:'white',padding:20,borderRadius:12 }}>
                                <h2 style={{ fontFamily: 'Montserrat', fontSize: 20, fontWeight: 700, color: '#212529', margin: 0 }}>
                                    Edit Campaign
                                </h2>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <button
                                        onClick={openDiscardForNavigation}
                                        style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, padding: '0 20px', borderRadius: 8, border: '1px solid #212529', background: '#fff', color: '#212529', cursor: 'pointer', height: 36 }}
                                    >
                                        Discard
                                    </button>

                                    {/* Validate and Save — normal / loading */}
                                    <button
                                        onClick={handleValidateAndSave}
                                        disabled={isSaving}
                                        style={{
                                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                                            padding: '0 20px', borderRadius: 8,
                                            border:      isSaving ? '1px solid #CED4DA' : 'none',
                                            background:  isSaving ? '#E9ECEF'           : '#212529',
                                            color:       isSaving ? '#6C757D'           : '#fff',
                                            cursor: isSaving ? 'not-allowed' : 'pointer',
                                            height: 36, display: 'flex', alignItems: 'center', gap: 8,
                                            minWidth: 160, justifyContent: 'center',
                                            transition: 'background 0.2s, color 0.2s, border 0.2s',
                                        }}
                                    >
                                        Validate and Save
                                        {isSaving && <SpinnerIcon />}
                                    </button>
                                </div>
                            </div>

                            {/* ── Basic Info ── */}
                            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 20, overflow: 'hidden' }}>
                                <div style={{ background: '#F8F9FA', padding: '12px 20px', fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#212529', borderBottom: '1px solid #E9ECEF' }}>
                                    Basic Info
                                </div>
                                <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

                                    {/* Campaign Name */}
                                    <div>
                                        <label style={{ display: 'block', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>
                                            Campaign Name <span style={{ color: '#DC3545' }}>*</span>
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                value={campaignName}
                                                onChange={e => { setCampaignName(e.target.value); setFieldErrors(p => ({ ...p, campaignName: '' })); }}
                                                placeholder="Campaign Name"
                                                style={{
                                                    width: '100%', height: 40, padding: '0 36px 0 12px',
                                                    border: `1px solid ${fieldErrors.campaignName ? '#DC3545' : '#6C757D'}`,
                                                    borderRadius: 8, fontFamily: 'Montserrat', fontSize: 13,
                                                    color: '#212529', outline: 'none', boxSizing: 'border-box', background: '#fff',
                                                }}
                                            />
                                            {campaignName ? (
                                                <button onClick={() => setCampaignName('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6C757D', padding: 0 }}>
                                                    <X size={16} />
                                                </button>
                                            ) : (
                                                <ChevronDown size={16} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#6C757D', pointerEvents: 'none' }} />
                                            )}
                                        </div>
                                        {fieldErrors.campaignName && (
                                            <p style={{ color: '#DC3545', fontFamily: 'Montserrat', fontSize: 11, marginTop: 4, margin: '4px 0 0' }}>
                                                {fieldErrors.campaignName}
                                            </p>
                                        )}
                                    </div>

                                    {/* Promotional Period */}
                                    <div>
                                        <label style={{ display: 'block', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 6 }}>
                                            Promotional Period <span style={{ color: '#DC3545' }}>*</span>
                                        </label>
                                        <CalendarRangeInput
                                            name="promotionalPeriod"
                                            startDate={startDate}
                                            endDate={endDate}
                                            enableTime
                                            borderColor={fieldErrors.promotionalPeriod ? '#DC3545' : '#6C757D'}
                                            onChange={(e) => {
                                                setStartDate(e.target.value.start);
                                                setEndDate(e.target.value.end);
                                                setFieldErrors(p => ({ ...p, promotionalPeriod: '' }));
                                            }}
                                        />
                                        {fieldErrors.promotionalPeriod && (
                                            <p style={{ color: '#DC3545', fontFamily: 'Montserrat', fontSize: 11, marginTop: 4, margin: '4px 0 0' }}>
                                                {fieldErrors.promotionalPeriod}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ── Campaign Items ── */}
                            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'unset' }}>
                                <div style={{ background: '#F8F9FA', padding: '12px 20px', fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#212529', borderBottom: '1px solid #E9ECEF', borderRadius: '12px 12px 0 0' }}>
                                    Campaign Items
                                </div>
                                <div className="entities-tab" style={{ padding: '16px 20px' }}>

                                    {/* Toolbar */}
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #6C757D', borderRadius: 8, padding: '0 12px', height: 40, background: '#fff', width: 280 }}>
                                            <Search size={18} color="#6C757D" />
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                value={search}
                                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                                                style={{ border: 'none', outline: 'none', width: '100%', fontSize: 13, fontFamily: 'Montserrat', background: 'transparent' }}
                                            />
                                            {search && (
                                                <button onClick={() => { setSearch(''); setPage(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0 }}>
                                                    <X size={14} color="#6C757D" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Condition Filter */}
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                ref={filterBtnRef}
                                                onClick={() => { setPendingNew(appliedNew); setPendingUsed(appliedUsed); setFilterOpen(p => !p); }}
                                                style={{ height: 40, width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: filterActive ? '1px solid #0D6EFD' : '1px solid #6C757D', borderRadius: 8, background: filterActive ? '#CFE2FF' : '#fff', cursor: 'pointer' }}
                                            >
                                                {filterActive ? <FilterActiveIcon /> : <FilterInactiveIcon />}
                                            </button>

                                            {filterOpen && (
                                                <div
                                                    ref={filterPopupRef}
                                                    style={{ width:400,padding:20,position: 'absolute', top: 48, left: 0, zIndex: 200, background: '#fff', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: 220, padding: 16 }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 12, color: '#212529', marginBottom: 12 }}>Condition</div>
                                                    <div style={{ display: 'flex',justifyContent:'space-evenly', gap: 10, marginBottom: 10 }}>
                                                        <label style={{ display: 'flex', alignItems: 'left', gap: 8, cursor: 'pointer', fontFamily: 'Montserrat', fontSize: 12, color: '#212529',padding:10,width:'50%' }}>
                                                            <YellowCheckbox checked={pendingNew}  onChange={() => setPendingNew(p => !p)} />
                                                            New
                                                        </label>
                                                        <label style={{ display: 'flex', alignItems: 'left', gap: 8, cursor: 'pointer', fontFamily: 'Montserrat', fontSize: 12, color: '#212529',padding:10,width:'50%' }}>
                                                            <YellowCheckbox checked={pendingUsed} onChange={() => setPendingUsed(p => !p)} />
                                                            Used
                                                        </label>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <button onClick={clearFilter} style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: '#5A6A7D', padding: 0 }}>Clear</button>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <button onClick={() => setFilterOpen(false)} style={{ fontFamily: 'Montserrat', fontSize: 12,display:'flex',alignItems:'center',justifyContent:'center', fontWeight: 600, padding: '10px',height:34,width:100, borderRadius: 8, border: '1px solid #212529', background: '#fff', color: '#212529', cursor: 'pointer' }}>Cancel</button>
                                                            <button onClick={applyFilter} style={{ fontFamily: 'Montserrat', fontSize: 12,display:'flex',alignItems:'center',justifyContent:'center', fontWeight: 600, padding: '10px',height:34,width:100, borderRadius: 8, border: 'none', background: '#212529', color: '#fff', cursor: 'pointer' }}>Confirm</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="table-wrapper" style={{ border: 'none' }}>
                                        {itemsLoading ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 12, fontFamily: 'Montserrat' }}>
                                                <div style={{ width: 32, height: 32, border: '4px solid #f3f3f3', borderTop: '4px solid #FFEB00', borderRadius: '50%', animation: 'ec-spin 0.8s linear infinite' }} />
                                                <span style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>Loading items...</span>
                                            </div>
                                        ) : (
                                        <table className="entities-table" style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat', fontSize: 13 }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ padding: '10px', background: '#E9ECEF', width: 40 }}>
                                                        <YellowCheckbox
                                                            checked={allPageSelected}
                                                            onChange={toggleAll}
                                                        />
                                                    </th>
                                                    <SortableCol label="Listing Model" field="model"     sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                                                    <SortableCol label="Listing Item"  field="storage"   sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                                                    <SortableCol label="Condition"     field="condition" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
                                                    {showOverlapCol && (
                                                        <SortableCol label="Overlapping Campaign" field="overlapCampaign" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} style={{ minWidth: 200 }} />
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pageData.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={showOverlapCol ? 5 : 4} style={{ textAlign: 'center', padding: '40px 0', color: '#6C757D', fontSize: 13 }}>
                                                            No items found.
                                                        </td>
                                                    </tr>
                                                ) : pageData.map((item, idx) => (
                                                    <tr key={item.id} style={{ borderBottom: '1px solid #F2F2F2', background: idx % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                                                        <td style={{ padding: '10px', width: 40 }}>
                                                            <YellowCheckbox
                                                                checked={!!selected[item.id]}
                                                                onChange={() => setSelected(prev => {
                                                                    const n = { ...prev };
                                                                    if (n[item.id]) delete n[item.id]; else n[item.id] = true;
                                                                    return n;
                                                                })}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '10px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <img src={item.image || noImage} alt={item.model} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} onError={e => { e.target.src = noImage; }} />
                                                                <span style={{ fontWeight: 400, color: '#212529', fontSize: 12 }}>{item.model || '—'}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <img src={item.image || noImage} alt={item.model} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} onError={e => { e.target.src = noImage; }} />
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                                    <span style={{ fontSize: 12, color: '#212529' }}>{item.storage || '—'}</span>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                        <ColorCircle color={item.color_code || '#ccc'} />
                                                                        <span style={{ fontSize: 11, color: '#6C757D' }}>{item.color || '—'}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px' }}>
                                                            <ConditionBadge condition={item.condition} />
                                                        </td>
                                                        {showOverlapCol && (
                                                            <td style={{ padding: '10px', fontFamily: 'Montserrat', fontSize: 11, color: item.overlapCampaign ? '#DC3545' : '#6C757D' }}>
                                                                {item.overlapCampaign || '—'}
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                    {filtered.length > 0 && (
                                        <div className="pagination">
                                            <div className="pagination-info">{`${rangeStart} - ${rangeEnd} of ${filtered.length}`}</div>
                                            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* ── Selected Items Floating Bar ── */}
            {selectedCount > 0 && (
                <div style={{
                    position: 'fixed', bottom: 24, left: '65%', transform: 'translateX(-50%)',
                    display: 'flex', alignItems: 'center', gap: 16,justifyContent:'space-between',
                    background: '#fff', borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                    padding: '10px',
                    zIndex: 900, whiteSpace: 'nowrap',width:424
                }}>
                    
                    <div style={{ display: 'flex',height:34, alignItems: 'center', gap: 8, borderRadius: '8px', padding: '10px', border: '1px solid #212529' }}>
                        <span style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, color: '#212529' }}>
                            {selectedCount} Selected
                        </span>
                        <button
                            onClick={() => setSelected({})}
                            title="Clear selection"
                            style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#212529' }}
                        >
                            <X size={18} color="#212529" />
                        </button>
                    </div>
                    <button
                        onClick={() => setShowRemoveModal(true)}
                        style={{
                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                            padding: '10px', borderRadius: 8,display:'flex',alignItems:'center',
                            border: 'none', background: '#212529', color: '#fff',
                            cursor: 'pointer', height: 34,
                        }}
                    >
                        Remove from the Campaign
                    </button>
                </div>
            )}

            {/* ── Remove Confirmation Modal ── */}
            {showRemoveModal && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setShowRemoveModal(false)}
                >
                    <div
                        style={{ background: '#fff', borderRadius: 12, padding: '20px 25px', width: 650, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', position: 'relative' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 0 }}>
                            <button onClick={() => setShowRemoveModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#212529', display: 'flex', alignItems: 'center', padding: 0 }}>
                                <X size={18} />
                            </button>
                        </div>
                        <h5 style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 20, color: '#212529', marginBottom: 10, marginTop: 10 }}>
                            Confirm to remove these Items from the Campaign?
                        </h5>
                        <p style={{ fontFamily: 'Montserrat', fontSize: 14, color: '#6C757D', marginBottom: 24, lineHeight: '1.6' }}>
                            Once removed, these items will no longer be eligible for Promotional Pricing and will remain at their Normal Prices. This action cannot be undone.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button
                                onClick={() => setShowRemoveModal(false)}
                                style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, height: 34, minWidth: 100, padding: '0 16px', borderRadius: 8, border: '1px solid #212529', background: '#fff', color: '#212529', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={openDiscardForRemove}
                                style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, height: 34, minWidth: 100, padding: '0 16px', borderRadius: 8, border: 'none', background: '#212529', color: '#fff', cursor: 'pointer' }}
                            >
                                Discard
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Discard Confirmation Modal ── */}
            <DiscardConfirmationModal
                isOpen={showDiscardModal}
                onCancel={() => setShowDiscardModal(false)}
                onConfirm={handleDiscardConfirm}
                entityName="Campaign"
            />
        </div>
    );
}
